import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppProviders } from "./AppProviders";
import { Shell } from "./Shell";
import { Login } from "./Login";
import { Register } from "./Register";
import { PasswordResetRequest } from "./PasswordResetRequest";
import { PasswordResetConfirm } from "./PasswordResetConfirm";
import { ChangePassword } from "./ChangePassword";
import { StatsPage } from "./StatsPage";
import { AdminLayout } from "../admin/AdminLayout";
import { isAuthenticated } from "../api/auth";
import "./app.css";

const AppContent = (): JSX.Element => {
    // Check authentication status
    // CRITICAL: Don't retry on 5xx errors (like 503) to avoid request loops
    const { data: isAuth = false, isLoading, error } = useQuery<boolean>({
        queryKey: ["auth", "status"],
        queryFn: isAuthenticated,
        retry: (_failureCount, error) => {
            // Never retry authentication checks - if server is down, don't hammer it
            // Also don't retry on 5xx errors (503, 500, etc.) as they indicate server issues
            if (error && typeof error === "object" && "response" in error) {
                const httpError = error as { response?: { status?: number } };
                if (httpError.response?.status && httpError.response.status >= 500) {
                    return false; // Don't retry server errors
                }
                if (httpError.response?.status && httpError.response.status >= 400 && httpError.response.status < 500) {
                    return false; // Don't retry client errors either
                }
            }
            return false; // Never retry auth checks
        },
        retryDelay: 0,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnMount: false, // Don't refetch on mount if we have cached data
        refetchOnWindowFocus: false, // Already disabled globally, but explicit here
        refetchOnReconnect: false // Don't refetch when connection is restored
    });

    // If there's an error (like 503), treat as not authenticated but don't show error
    // This prevents infinite loops when server is down
    // User will see login page, which is correct behavior
    if (isLoading) {
        return <div className="app-loading">Loading…</div>;
    }

    // Treat errors (like 503) as not authenticated - show login page
    // This is safer than trying to render authenticated routes when server is down
    if (!isAuth || error) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/password-reset" element={<PasswordResetRequest />} />
                <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />
                {/* Legacy email links used /reset-password/... — keep aliases */}
                <Route path="/reset-password" element={<PasswordResetRequest />} />
                <Route path="/reset-password/confirm" element={<PasswordResetConfirm />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<Shell />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/admin/*" element={<AdminLayout />} />
            <Route path="/password-reset" element={<PasswordResetRequest />} />
            <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />
            <Route path="/reset-password" element={<PasswordResetRequest />} />
            <Route path="/reset-password/confirm" element={<PasswordResetConfirm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

const App = (): JSX.Element => {
    return (
        <AppProviders>
            <BrowserRouter>
                <Suspense fallback={<div className="app-loading">Loading…</div>}>
                    <AppContent />
                </Suspense>
            </BrowserRouter>
        </AppProviders>
    );
};

export default App;